import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  TextField,
  Button,
  IconButton,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AssignmentReturnRoundedIcon from "@mui/icons-material/AssignmentReturnRounded";
import VerifiedPopup from "./VerifiedPopup";
import { useVerifiedStore } from "../store/useVerifiedStore";
import CustomDropdown from "./CustomDropdown";

type AssignmentPopupProps = {
  selectedValue: any;
  open: boolean;
  onClose: () => void;
  userRoles: any;
  onAssignmentHandle: any;
  claimData?: any;
  requireReason?: boolean;
};

const AssignmentPopup = (props: AssignmentPopupProps) => {
  const {
    selectedValue,
    open,
    onClose,
    userRoles,
    onAssignmentHandle,
    claimData,
    requireReason = false,
  } = props;
  const showVerified = useVerifiedStore((state) => state.showVerified);
  const setShowVerified = useVerifiedStore((state) => state.setShowVerified);

  const isAssignMe = selectedValue === "Assign Me";
  const isAssignSomeone = selectedValue === "Assign Someone";
  const isReassignMe = selectedValue === "Reassign to me";
  const isReassignSomeone = selectedValue === "Reassign Someone";
  const isUnassign = selectedValue === "Unassign";
  const backToConsultant = selectedValue === "Back to consultant";
  const backToAdmin = selectedValue === "Back to admin";
  const backToManager = selectedValue === "Back to manager";

  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [assignmentReason, setAssignmentReason] = useState("");

  useEffect(() => {
    if (!open) {
      setSelectedUser(null);
      setAssignmentReason("");
    }
  }, [open]);

  const label = isAssignMe
    ? "Do you want to Assign Yourself?"
    : isAssignSomeone
    ? "Do you want to Assign"
    : isReassignMe
    ? "Do you want to take over this claim?"
    : isReassignSomeone
    ? "Do you want to reassign this claim?"
    : isUnassign
    ? "Do you want to unassign this claim?"
    : backToConsultant
    ? `Do you want to assign back to ${claimData.data.consultantName}`
    : backToAdmin
    ? `Do you want to assign back to ${claimData.data.adminName}`
    : backToManager
    ? `Do you want to assign back to ${claimData.data.managerName}`
    : "";

  const dialogTitle = isAssignMe
    ? "Assign this claim to yourself?"
    : isAssignSomeone
    ? "Assign this claim"
    : isReassignMe
    ? "Take over this claim?"
    : isReassignSomeone
    ? "Reassign this claim"
    : isUnassign
    ? "Unassign this claim?"
    : label.replace("Do you want to assign back to", "Return claim to") + "?";

  const dialogDescription = requireReason
    ? "Add a clear reason for this assignment change."
    : "Please confirm this assignment before continuing.";

  const getUserDisplayName = (item: any) => {
    if (item.first_name && item.last_name) return `${item.first_name} ${item.last_name}`;
    if (item.first_name) return item.first_name;
    if (item.last_name) return item.last_name;
    return item.name;
  };

  const handleChange = (e: any) => {
    const selectedName = e.target.value;
    const user = userRoles.find((item: any) => getUserDisplayName(item) === selectedName);
    setSelectedUser(user);
  };

  const isConfirmDisabled =
    ((isAssignSomeone || isReassignSomeone) && !selectedUser) ||
    (backToManager && !claimData?.data?.managerUserId) ||
    (requireReason && !assignmentReason.trim());

  const handleConfirm = () => {
    const userIdToAssign = isAssignSomeone
      ? selectedUser?.userId
      : isReassignSomeone
      ? selectedUser?.userId
      : backToConsultant
      ? claimData.data.consultantUserId
      : backToAdmin
      ? claimData.data.adminUserId
      : backToManager
      ? claimData.data.managerUserId
      : isUnassign
      ? null
      : undefined;
    onAssignmentHandle(userIdToAssign, assignmentReason.trim());
  };

  const confirmLabel = isAssignSomeone
    ? "Assign claim"
    : isReassignSomeone
    ? "Reassign claim"
    : isReassignMe
    ? "Reassign to me"
    : isUnassign
    ? "Unassign claim"
    : isAssignMe
    ? "Assign to me"
    : "Return claim";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="assignment-dialog-title"
      aria-describedby="assignment-dialog-description"
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(15, 23, 42, 0.46)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
          },
        },
        paper: {
          sx: {
            width: "min(480px, calc(100% - 32px))",
            maxWidth: "none",
            borderRadius: 3,
            overflow: "hidden",
            backgroundColor: "#FFFFFF",
            boxShadow: "0 24px 64px rgba(15, 23, 42, 0.26)",
          },
        },
      }}
    >
      <Box
        sx={{
          px: { xs: 2.5, sm: 3.5 },
          pt: 3.5,
          pb: 2,
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          position: "relative",
          borderTop: "5px solid",
          borderColor: "primary.main",
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            color: "primary.main",
            backgroundColor: "rgba(236, 110, 0, 0.10)",
          }}
        >
          <AssignmentReturnRoundedIcon />
        </Box>
        <Box sx={{ pr: 4 }}>
          <Typography
            id="assignment-dialog-title"
            fontSize={{ xs: 20, sm: 22 }}
            fontWeight={700}
            lineHeight={1.3}
            color="#172033"
          >
            {dialogTitle}
          </Typography>
          <Typography
            id="assignment-dialog-description"
            mt={0.75}
            fontSize={13.5}
            lineHeight={1.5}
            color="#667085"
          >
            {dialogDescription}
          </Typography>
        </Box>
        <IconButton
          aria-label="Close assignment dialog"
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            color: "#667085",
            backgroundColor: "#F2F4F7",
            "&:hover": { backgroundColor: "#E4E7EC" },
          }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          px: { xs: 2.5, sm: 3.5 },
          pt: 1,
          pb: 3.5,
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
        }}
      >
          {(isAssignSomeone || isReassignSomeone) && (
            <CustomDropdown
              value={selectedUser ? getUserDisplayName(selectedUser) : ""}
              onChange={handleChange}
              options={userRoles.map(getUserDisplayName)}
              placeholder="Choose the user"
              width="100%"
              background="#f7f7f7"
              boxShadow="none"
            />
          )}

          {requireReason && (
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Reason for assignment change"
              value={assignmentReason}
              onChange={(event) => setAssignmentReason(event.target.value)}
              required
              inputProps={{ maxLength: 1000 }}
              helperText={`${assignmentReason.length}/1000 characters`}
              autoFocus
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: "#FCFCFD",
                  alignItems: "flex-start",
                },
                "& .MuiFormHelperText-root": {
                  mx: 0,
                  textAlign: "right",
                  color: "#98A2B3",
                },
              }}
            />
          )}

          <Box
            display="flex"
            justifyContent="flex-end"
            gap={1.5}
            width="100%"
            flexDirection={{ xs: "column-reverse", sm: "row" }}
          >
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                minWidth: 120,
                height: 42,
                borderRadius: 2,
                borderColor: "#D0D5DD",
                color: "#344054",
                backgroundColor: "#FFFFFF",
                fontWeight: 700,
                textTransform: "none",
                "&:hover": {
                  borderColor: "#98A2B3",
                  color: "#1D2939",
                  backgroundColor: "#F9FAFB",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={isConfirmDisabled}
              sx={{
                minWidth: 140,
                height: 42,
                borderRadius: 2,
                boxShadow: "none",
                color: "#FFFFFF",
                backgroundColor: "#EC6E00",
                fontWeight: 700,
                textTransform: "none",
                "&:hover": {
                  boxShadow: "none",
                  backgroundColor: "#D96300",
                },
                "&.Mui-disabled": {
                  color: "#B54708",
                  backgroundColor: "#FDE6D2",
                },
              }}
            >
              {confirmLabel}
            </Button>
          </Box>
      </DialogContent>
      <VerifiedPopup
        label="Assigned"
        open={showVerified}
        onClose={() => {
          setShowVerified(false);
          onClose();
        }}
      />
    </Dialog>
  );
};

export default AssignmentPopup;
